"use client";

import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Accordion } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ArrowLink } from "./ArrowLink";
import React from "react";
import type { JSX, ReactNode } from "react";

interface MobileNavSubSectionItem {
  title: string;
  href: string;
  description?: string;
  badge?: string;
}

interface MobileNavSubSectionProps {
  title: string;
  items?: MobileNavSubSectionItem[];
  cta?: { text: string; href: string };
  children?: ReactNode;
  titleClassName?: string;
}

export const MobileNavSubSection = ({
  title,
  items,
  cta,
  children,
  titleClassName,
}: MobileNavSubSectionProps): JSX.Element => {
  return (
    <AccordionItem value={`section-${title}`} className="border-none">
      <AccordionTrigger
        direction="right"
        className={cn(
          "appearance-none bg-transparent border-none w-full justify-between px-xl py-lg text-sm-semibold hover:no-underline [&_svg]:text-current",
          "text-brand-primary hover:text-brand-secondary",
          titleClassName,
        )}
      >
        {title}
      </AccordionTrigger>
      <AccordionContent className="pb-0">
        {children ? (
          <Accordion type="multiple" className="w-full border-none">
            {children}
          </Accordion>
        ) : (
          <ul className="list-none p-0 m-0 pb-md flex flex-col gap-xs">
            {items?.map(({ title: itemTitle, href, description, badge }) => (
              <li key={href}>
                <a
                  href={href}
                  className="block py-lg px-xl no-underline hover:no-underline hover:bg-gray-light-50 rounded-xs transition-colors"
                >
                  <span className="inline-flex items-center gap-md">
                    <span className="text-sm-medium text-tertiary">
                      {itemTitle}
                    </span>
                    {badge && (
                      <Badge variant="brand" size="sm">
                        {badge}
                      </Badge>
                    )}
                  </span>
                  {description && (
                    <span className="block text-sm-regular text-quaternary mt-xs">
                      {description}
                    </span>
                  )}
                </a>
              </li>
            ))}
            {cta && (
              <li className="px-xl pt-4xl pb-md">
                <ArrowLink href={cta.href}>{cta.text}</ArrowLink>
              </li>
            )}
          </ul>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};
